<template>
  <base-page-layout title="Previsão de vendas clube" :filters-quantity="countFiltersSelected">
    <template v-if="activeModule" #custom-filters>
      <maria-filters :filters="filters" :filters-quantity="countFiltersSelected" @update-data="setNewData" @clear-filters="clearFilters" />
    </template>
    <v-row>
      <v-col cols="3">
        <v-row>
          <v-col cols="12">
            <v-skeleton-loader v-if="paginationLoading" max-width="100" height="30" type="card" />
            <span v-else class="page-title">{{ totalClient | localeString(0) }}</span>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-chip-group v-if="filters.selectedSegments.length" column active-class="primary--text">
              <v-chip
                v-for="(segment, index) in filters.selectedSegments"
                :key="`${segment.name}-${index}`"
                small
                close
                :ripple="false"
                @click:close="removeChipAndUpdate(index)"
              >
                <v-tooltip right>
                  <template #activator="{ on }">
                    <span v-on="on">
                      {{ segment.name && segment.name.length > 20 ? segment.name.substring(0, 20) + '...' : segment.name }}
                    </span>
                  </template>
                  <span>{{ segment.name }}</span>
                </v-tooltip>
                <span class="texto-corrido" />
              </v-chip>
            </v-chip-group>
            <p v-if="!filters.selectedSegments.length" class="texto-corrido ma-0">Clientes da base de cadastro.</p>
          </v-col>
          <v-col>
            <p class="texto-corrido">
              Essa página mostra uma previsão de vendas para o segmento selecionado. Você pode usar os filtros no canto superior direito para escolher em quais
              lojas e qual periodo você gostaria de analisar, por exemplo.
            </p>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="9">
        <maria-cards v-if="sales.length && activeModule" :filters="filters" :total-client="totalClient" :update-cards="updateCards" />
        <v-card v-if="sales.length && activeModule" class="maria-sales-card">
          <v-card-title>
            <span class="pa-2 table-card-title">Produtos que vão ser comprados</span>
            <v-spacer />
            <v-tooltip bottom max-width="254px">
              <template #activator="{ on }">
                <a v-on="on">
                  <export-data class="mx-2" :data-fetcher="getDataToExport" filename="produtos_previsao_vendas" ghost />
                </a>
              </template>
              <span>
                A exportação de itens a serem vendidos respeita os filtros adicionados à tela e à ordenação realizada na tabela. Por uma limitação técnica são
                exportadas até 150 linhas.
              </span>
            </v-tooltip>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :loading="paginationLoading"
            :server-items-length="maxRecords"
            :items="sales"
            no-data-text="Não existem dados para serem exibidos para essa combinação de filtros. Tente mudar suas seleções."
            :options.sync="options"
            :footer-props="{ itemsPerPageOptions: [5, 10, 15, 50] }"
          >
            <template #item="{ item }">
              <tr>
                <td>
                  <router-link class="link" :to="{ path: `/produtos/detalhes-produto/${item.product.sku}` }">
                    {{ item.product.name }}
                  </router-link>
                </td>
                <td class="texto-corrido">
                  R${{ item.estimated_unitary_price_lower | localeString(2) }} - R${{ item.estimated_unitary_price_upper | localeString(2) }}
                </td>
                <td class="texto-corrido text-right">
                  {{ item.estimated_clients_count | localeString(0) }} <mf-quick-actions :key="item.product.sku" v-bind="getQuickActionProps(item)" />
                </td>
                <td class="texto-corrido text-right">
                  {{ item.estimated_products_quantity_lower | localeString(0) }} - {{ item.estimated_products_quantity_upper | localeString(0) }}
                </td>
                <td class="texto-corrido text-right">
                  {{ item.estimated_coupons_count | localeString(0) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
        <v-card v-else class="maria-sales-card elevation-0" height="400">
          <v-card-title>
            <span class="pa-2 card-title">Produtos que vão ser comprados</span>
          </v-card-title>
          <empty-card :message="infoMessage" height="100%" width="400">
            <template #custom-content>
              <vue-ellipse-progress
                v-if="showProgress"
                :progress="fidelityPercentual"
                :size="250"
                :empty-color="emptyColorFill"
                :thickness="6"
                :empty-thickness="27"
                :legend-value="percentualFaturamentoFidelidade"
                color="marca-1"
                color-fill="transparent"
                animation="loop 700 1000"
                font-size="2.7rem"
                font-color="#00b395"
                line-mode="out-over"
              >
                <template #default="{ counterTick: { currentValue } }">
                  <v-row justify="end">
                    <v-col cols="12" class="px-0">
                      <span class="percentual">
                        {{ currentValue }}
                        <div class="percentual-box">
                          <div class="mb-3">/</div>
                          <b class="mt-3 side-text"> 30% </b>
                        </div>
                      </span>
                    </v-col>
                  </v-row>
                </template>
                <template #legend-caption>
                  <p slot="legend-caption" class="maria-sales-legend-caption ma-0 pt-3">Faturamento identificado</p>
                </template>
              </vue-ellipse-progress>
            </template>
          </empty-card>
        </v-card>
      </v-col>
    </v-row>
  </base-page-layout>
</template>

<script>
import { isEqual } from 'lodash'
import { QUERY_GET_SALES_ESTIMATION, QUERY_GET_SEGMENTATION_CLIENTS } from '../../graphql'
import { DateTime } from 'luxon'
import ExportData from '@/components/export-data/ExportData.vue'
import { mapGetters, mapState, mapActions } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { criteriaGroup, isInSegmentCriteria, willPurchaseProductsCriteria } from '@/components/quick-actions/segmentationGroups.js'
/* eslint-disable */
const MIN_QNT_DEFAULT = 5
const DATE_DEFAULT = [DateTime.now().toFormat('yyyy-MM-dd'), DateTime.now().plus({ days: +7 }).toFormat('yyyy-MM-dd')]
const SEGMENTS_DEFAULT = []
const UNITS_DEFAULT = []
const UNITS_CONFIGURATION_DEFAULT = []
const PRODUCTS_DEFAULT = []

export default {
  name: 'MariaSales',
  components: {
    EmptyCard: () => import('@/components/EmptyCard.vue'),
    BasePageLayout: () => import('@/components/BasePageLayout.vue'),
    MariaFilters: () => import('./MariaFilters.vue'),
    MariaCards: () => import('./MariaCards.vue'),
    ExportData,
  },
  data: () => ({
    showProgress: true,
    updateCards: false,
    segmentationClientsNumber: 0,
    DateTime: DateTime,
    filters: {
      date: DATE_DEFAULT,
      minQntPurchaseDays: MIN_QNT_DEFAULT,
      selectedSegments: SEGMENTS_DEFAULT,
      units: UNITS_DEFAULT,
      unitsConfiguration: UNITS_CONFIGURATION_DEFAULT,
      products: PRODUCTS_DEFAULT,
    },
    options: {
      groupBy: [],
      groupDesc: [],
      itemsPerPage: 10,
      multiSort: false,
      mustSort: false,
      page: 1,
      sortBy: [],
      sortDesc: [],
    },
    emptyColorFill: {
      radial: true,
      colors: [
        {
          color: '#00b395',
          offset: '90',
          opacity: '0.3',
        },
        {
          color: '#00b395',
          offset: '99.9',
          opacity: '0.07',
        },
        {
          color: '#00b395',
          offset: '100',
          opacity: '0.01',
        },
      ],
    },
    maxRecords: 0,
    paginationLoading: false,
    sales: [],
  }),
  computed: {
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
      qtdeCadastrados: (state) => state.clientes.quantidadeClientesCadastrados,
    }),
    ...mapGetters({
      quickAction: 'getQuickAction',
      percentualFaturamentoFidelidade: 'mariaPercentualFaturamentoFidelidade',
    }),
    filterDatesWithDemo() {
      if (this.$isOnTestOrDemoAccount()) {
        return [
          DateTime.fromFormat(String(this.lastUpdate.last_updated), 'yyyyMMdd').toFormat('yyyy-MM-dd'),
          DateTime.fromFormat(String(this.lastUpdate.last_updated), 'yyyyMMdd').plus({ days: +7 }).toFormat('yyyy-MM-dd'),
        ]
      }
      return this.filters.date
    },
    totalClient() {
      return this.segmentationClientsNumber === 0 ? this.qtdeCadastrados : this.segmentationClientsNumber
    },
    activeModule() {
      return this.$getAllowedModules()['forecast']
    },
    headers() {
      return [
        {
          text: 'Produto',
          align: 'left',
          width: '25%',
          sortable: false,
        },
        {
          text: 'Preço mais efetivo',
          align: 'left',
          value: 'estimated_unitary_price_upper',
          width: '15%',
        },
        {
          text: 'Previsão de clientes',
          align: 'right',
          value: 'estimated_clients_count',
          width: '15%',
        },
        {
          text: 'Previsão de itens',
          align: 'right',
          value: 'estimated_products_quantity_upper',
          width: '15%',
        },
        {
          text: 'Quant. cupons',
          align: 'right',
          value: 'estimated_coupons_count',
          width: '15%',
        },
      ]
    },
    countFiltersSelected() {
      return Object.keys(this.filters).reduce((filterCount, filter) => {
        if (this.filters[filter]?.length || (filter === 'minQntPurchaseDays' && this.filters[filter] >= 0)) {
          filterCount += 1
        }
        return filterCount
      }, 0)
    },
    infoMessage() {
      return this.activeModule && !this.showProgress
        ? 'Não existem dados a serem exibidos para essa combinação de filtros. Tente mudar suas seleções ou entre em contato com o suporte pelo chat.'
        : 'Para ter acesso a nossa inteligência artificial, você precisa ter pelo menos uma loja com 30% do faturamento identificado.'
    },
    filter() {
      return {
        date: DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM'),
        group_field: 'unidade',
      }
    },
    fidelityPercentual() {
      const value = this.percentualFaturamentoFidelidade / 0.3 || 0
      return value > 100 ? 100 : value
    },
  },
  watch: {
    options(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) this.setNewData()
    },
    percentualFaturamentoFidelidade() {
      if (this.activeModule) {
        setTimeout(() => {
          this.setNewData()
        }, 2000)
      }
    },
    filters: {
      handler(filters) {
        if (filters.selectedSegments.length) {
          this.searchNumberOfClients()
        } else {
          this.segmentationClientsNumber = 0
        }
      },
      deep: true,
    },
    $route: {
      handler() {
        const quickAction = Boolean(this.$route.query.quick_action)
        if (quickAction) {
          this.prebuildAutoMaria()
        }
      },
      deep: true,
    },
  },
  beforeMount() {
    this.$store.dispatch('getStates', ['getLastUpdate']).then(() => {
      this.mariaPercentualFaturamentoFidelidade(this.filter)
    })
    this.quantidadeClientesCadastrados()
  },
  mounted() {
    this.prebuildAutoMaria()
  },
  methods: {
    ...mapActions(['setIsIncludingSegments', 'unsetQuickAction']),
    ...mapWaitingActions({
      mariaPercentualFaturamentoFidelidade: 'percentual-faturamento-fidelidade',
      quantidadeClientesCadastrados: 'base-total',
    }),
    getData(itemsQty) {
      try {
        const itemsPerPage = itemsQty || this.options.itemsPerPage || 10
        this.paginationLoading = true
        return this.$apollo.query({
          query: QUERY_GET_SALES_ESTIMATION,
          variables: {
            page: this.options.page || 1,
            page_size: itemsPerPage,
            sort: this.options.sortBy,
            sort_direction: this.options.sortDesc && this.options.sortDesc.map((direction) => (direction ? 'DESC' : 'ASC')),
            segments_ids: this.filters.selectedSegments.map((segment) => segment._id),
            initial_date: this.filterDatesWithDemo[0],
            final_date: this.filterDatesWithDemo[1],
            min_qnt_purchase_days: Number(this.filters.minQntPurchaseDays),
            units: this.filters.units,
            products: this.filters.products.map((product) => product.sku),
          },
          fetchPolicy: 'no-cache',
        })
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar produtos', snackbarColor: 'error' })
      }
    },
    async setNewData(isFilter = false) {
      if (!this.activeModule) {
        return
      }
      if (isFilter) this.options.page = 1
      this.updateCards = isFilter

      // Unset quick actions when changing the filter
      this.setIsIncludingSegments(false)

      this.getData()
        .then(
          ({
            data: {
              salesEstimation: { sales, records, page },
            },
          }) => {
            this.paginationLoading = false
            this.sales = sales
            this.maxRecords = records
            this.page = page
            this.showProgress = false
            this.updateCards = false
          }
        )
        .catch(() => {
          this.paginationLoading = false
          this.$snackbar({ message: 'Erro ao buscar produtos', snackbarColor: 'error' })
        })
    },
    searchNumberOfClients() {
      try {
        this.$apollo
          .query({
            query: QUERY_GET_SEGMENTATION_CLIENTS,
            variables: {
              segments_ids: this.filters.selectedSegments.map((segment) => segment._id),
            },
            fetchPolicy: 'no-cache',
          })
          .then(({ data: { getSegmentsNumberOfClients } }) => {
            this.segmentationClientsNumber = getSegmentsNumberOfClients.total
          })
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar quantidade de clientes', snackbarColor: 'error' })
      }
    },
    async getDataToExport() {
      try {
        const {
          data: {
            salesEstimation: { sales },
          },
        } = await this.getData(150)
        this.paginationLoading = false

        const rows = sales.map((product) => {
          const {
            product: { sku, name },
            estimated_unitary_price_lower,
            estimated_unitary_price_upper,
            estimated_clients_count,
            estimated_products_quantity_lower,
            estimated_products_quantity_upper,
            estimated_coupons_count,
          } = product

          return [
            sku,
            name,
            `${estimated_unitary_price_lower.toFixed(2)} - ${estimated_unitary_price_upper.toFixed(2)}`,
            estimated_clients_count,
            `${parseInt(estimated_products_quantity_lower)} - ${parseInt(estimated_products_quantity_upper)}`,
            estimated_coupons_count,
          ]
        })
        const headers = ['id', 'nome', 'preço efetivo', 'previsao clientes', 'previsao items', 'qtde cupons']
        return { headers, rows }
      } catch (error) {
        this.$snackbar({ message: 'Erro ao exportar .csv', snackbarColor: 'error' })
      }
    },
    clearFilters() {
      this.options.page = 1
      this.filters.selectedSegments = SEGMENTS_DEFAULT
      this.filters.date = DATE_DEFAULT
      this.filters.units = UNITS_DEFAULT
      this.filters.unitsConfiguration = UNITS_CONFIGURATION_DEFAULT
      this.filters.minQntPurchaseDays = MIN_QNT_DEFAULT
      this.filters.products = PRODUCTS_DEFAULT
      this.segmentationClientsNumber = 0
      this.setNewData(true)
    },
    removeChipAndUpdate(chipIndex) {
      this.filters.selectedSegments.splice(chipIndex, 1)
      this.setNewData()
    },
    getQuickActionProps(item) {
      const [strStartDate, strEndDate] = this.filters.date.map((date) => DateTime.fromFormat(date, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'))
      const [startDate, endDate] = this.filters.date.map((date) => date.replace(/[^0-9]/g, ''))
      return {
        quickActionGroupId: `forecasting-will-purchase-product-${item?.product?.sku}`,
        shortDescription: `Clientes que vão comprar produto(s) específicos no período ${strStartDate} - ${strEndDate}`,
        longDescription: `Clientes que vão comprar produto(s) específicos no período ${strStartDate} - ${strEndDate}`,
        quickActionGroup: 'forecasting-will-purchase-products',
        quickActionGroupValue: item?.product?.sku,
        segmentationGroup: (products, logicOperator) => () => {
          return criteriaGroup([
            willPurchaseProductsCriteria(products, this.filters.unitsConfiguration, this.filters.minQntPurchaseDays, startDate, endDate, logicOperator),
            ...this.filters.selectedSegments.map((segment) => isInSegmentCriteria(segment)),
          ])
        },
      }
    },
    async prebuildAutoMaria() {
      if (!this.getIsQuickAction() || !this.quickAction?.segmentation) {
        return
      }

      this.filters.selectedSegments = [this.quickAction.segmentation.segment]

      this.unsetQuickAction()
    },
  },
}
</script>

<style lang="scss">
.maria-sales {
  .maria-sales-card {
    min-height: 250px;
  }
}

.maria-sales-legend-caption {
  color: $marca-1;
}

#header-shape-gradient {
  --color-stop: #f12c06;
  --color-bot: #faed34;
}
.percentual {
  position: relative;
  .percentual-box {
    position: absolute;
    right: -40px;
    bottom: 0;
    display: flex;
    .side-text {
      font-size: 14px;
    }
  }
}
</style>
